import React, { useContext, useState } from 'react';
import { useFormik } from 'formik';
import { alpha } from '@mui/material/styles';
import { Box, Button, TextField, Typography, useTheme, IconButton, Grid, Link, InputAdornment } from '@mui/material';
import { ColorModeContext } from '../../theme';
import { Link as RouterLink } from 'react-router-dom';
import { Brightness7, Brightness4, Visibility, VisibilityOff } from '@mui/icons-material';
import { AuthenticationSchema } from '../../schemas/authentication-schema';
import AuthContainerForm from '../../components/AuthContainerForm';
import { useAuth } from '../../hooks/useAuth';
import useAuthenticationService from '../../api/services/authenticationService';

// Helper function to check if a string is an email
const isEmail = (value) => {
  if (!value) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
};

const initialValues = {
  emailOrPhone: '',
  password: '',
};

const Login = () => {
  const { signIn } = useAuth();
  const { isLoading } = useAuthenticationService();
  const [showPassword, setShowPassword] = useState(false);

  const theme = useTheme();
  const colorMode = useContext(ColorModeContext);

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleFormSubmit = async (values, { setSubmitting }) => {
    try {
      const { emailOrPhone, password } = values;
      // Detect if input is email or phone number
      const isEmailInput = isEmail(emailOrPhone);
      
      if (isEmailInput) {
        await signIn(emailOrPhone, '', password);
      } else {
        await signIn('', emailOrPhone, password);
      }
    } catch (error) {

    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues,
    validationSchema: AuthenticationSchema,
    onSubmit: handleFormSubmit,
  });

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        px: 2,
        py: { xs: 1.5, md: 2 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        overflow: 'hidden',
        background: theme.palette.mode === 'dark'
          ? 'linear-gradient(180deg, #09111f 0%, #0f1c2f 100%)'
          : 'linear-gradient(180deg, #f4f8ff 0%, #eef4ff 100%)',
      }}
    >
      <Box display="flex" justifyContent="flex-end" alignItems="center" width="100%" maxWidth={1200} mx="auto">
        <IconButton
          onClick={colorMode.toggleColorMode}
          color="inherit"
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
            borderRadius: '12px',
            px: 1.5,
          }}
        >
          {theme.palette.mode === 'dark' ? (
            <>
              <Typography variant="body2" mr={1}>DARK</Typography>
              <Brightness7 />
            </>
          ) : (
            <>
              <Typography variant="body2" mr={1}>LIGHT</Typography>
              <Brightness4 />
            </>
          )}
        </IconButton>
      </Box>

      <AuthContainerForm title="LOGIN">
        <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
          <Box mb={1.5}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              Sign in with your registered phone number or email address.
            </Typography>
          </Box>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                label="Phone number or email"
                placeholder="e.g. 07XXXXXXXX or name@example.com"
                {...formik.getFieldProps('emailOrPhone')}
                error={formik.touched.emailOrPhone && Boolean(formik.errors.emailOrPhone)}
                helperText={formik.touched.emailOrPhone && formik.errors.emailOrPhone}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '14px',
                    minHeight: 48,
                    backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.03) : '#ffffff',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                variant="outlined"
                type={showPassword ? 'text' : 'password'}
                label="Password"
                {...formik.getFieldProps('password')}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '14px',
                    minHeight: 48,
                    backgroundColor: theme.palette.mode === 'dark' ? alpha('#ffffff', 0.03) : '#ffffff',
                  },
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={handleClickShowPassword}
                        edge="end"
                        color="inherit"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>

          <Box mt={2} display="flex" justifyContent="center">
            <Button
              type="submit"
              color="secondary"
              variant="contained"
              disabled={formik.isSubmitting || isLoading}
              sx={{
                minWidth: 200,
                py: 1.1,
                borderRadius: '14px',
                fontSize: '15px',
                fontWeight: 700,
                textTransform: 'none',
              }}
            >
              {formik.isSubmitting || isLoading ? 'Signing in...' : 'Sign in'}
            </Button>
          </Box>
        </form>

        <Box mt={1.5} display="flex" justifyContent="center">
          <Typography variant="body2" color="text.secondary">
            Have you forgotten your password?{' '}
            <Link
              to="/resetPassword"
              component={RouterLink}
              underline="hover"
              sx={{
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? '#90caf9' : theme.palette.primary.main,
                '&:hover': {
                  color: theme.palette.mode === 'dark' ? '#bbdefb' : theme.palette.primary.dark,
                },
              }}
            >
              Reset password
            </Link>
          </Typography>
        </Box>
      </AuthContainerForm>
    </Box>
  );
};

export default Login;
